function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import FocusTrap from 'focus-trap-react';
import { lightThemeObject } from "../../styleguide";
import SafeHtmlElement from "../../utils/SafeHtmlElement";
import useScrollLock from "./useScrollLock";
var CloseButton = styled.button.withConfig({
  displayName: "Modal__CloseButton",
  componentId: "sc-1sm9281-0"
})(["height:0;width:0;border:none;appearance:none;padding:0;margin:0;opacity:0;"]);
var Background = styled.aside.withConfig({
  displayName: "Modal__Background",
  componentId: "sc-1sm9281-1"
})(["position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;transform:translateZ(0);background-color:rgba(0,0,0,0.72);"]);
var Content = styled.div.withConfig({
  displayName: "Modal__Content",
  componentId: "sc-1sm9281-2"
})(["position:fixed;top:0;left:0;width:100%;height:100%;background-color:var(--bui-background,", ");box-shadow:0 0 10px 3px rgba(0,0,0,0.1);overflow-y:auto;-webkit-overflow-scrolling:touch;border-radius:0;@media screen and (min-width:768px){border-radius:10px;left:50%;top:50%;height:auto;transform:translate(-50%,-50%);width:550px;}"], lightThemeObject['--bui-background']);
/**
 * Modal is used to render a modal overlay with minimal styling (you must
 * style content yourself to add padding, etc.).
 *
 * Clicking outside the modal content, or pressing the escape
 * key closes the modal. For accessibility, you should also include at
 * least one button in the modal which closes the modal.
 */

var Modal = function Modal(_ref) {
  var children = _ref.children,
      label = _ref.label,
      open = _ref.open,
      onClose = _ref.onClose,
      className = _ref.className,
      _ref$initialFocus = _ref.initialFocus,
      initialFocus = _ref$initialFocus === void 0 ? '#close-modal' : _ref$initialFocus;
  var backgroundRef = useRef();
  var clickContext = useRef(); // Used to prevent modal from closing when dragging outside of the Content
  // put the modal ref into state using a callback so useScrollLock
  // can re-apply when the modal is rendered into the DOM.

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      modalRef = _useState2[0],
      setModalRef = _useState2[1];

  var modalRefCallback = useCallback(setModalRef, [setModalRef]);
  useScrollLock(open, modalRef);

  var handleClick = function handleClick(event) {
    if (modalRef && modalRef.contains(event.target)) {
      return;
    }

    if (clickContext.current === backgroundRef.current) {
      onClose(event);
    }
  };

  var handleMouseDown = function handleMouseDown(event) {
    clickContext.current = event.target;
  };

  useEffect(function () {
    var handleKeyDown = function handleKeyDown(event) {
      // Close when the escape key is pressed.
      if (event.keyCode === 27) {
        onClose(event);
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    } // On unmount, make sure to clean up even if the `open` prop didn't change


    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  if (open) {
    return /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("style", null, "\n            body.modal_open,\n            html.modal_open {\n              overflow: hidden;\n            }\n          "), /*#__PURE__*/React.createElement(FocusTrap, {
      focusTrapOptions: {
        initialFocus: initialFocus,
        fallbackFocus: '#close-modal'
      }
    }, /*#__PURE__*/React.createElement(Background, {
      "data-testid": "modal-background",
      onClick: handleClick,
      onMouseDown: handleMouseDown,
      "aria-modal": "true",
      tabIndex: "-1",
      "aria-label": label,
      ref: backgroundRef
    }, /*#__PURE__*/React.createElement(CloseButton, {
      "aria-label": "St\xE4ng Modal",
      id: "close-modal",
      onClose: onClose
    }), /*#__PURE__*/React.createElement(Content, {
      ref: modalRefCallback,
      role: "dialog",
      className: className,
      "data-testid": "modal-content"
    }, children)))), document.body);
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null);
};

Modal.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Content to render in the modal when it is opened
   */
  children: PropTypes.node.isRequired,

  /**
   * Used in a `aria-label` attribute on the modal to describe its content to screen readers
   */
  label: PropTypes.string.isRequired,

  /**
   * Boolean to set if the modal is open or closed
   */
  open: PropTypes.bool.isRequired,

  /**
   * When the user clicks outside of the content area, or presses the escape key, the onClose callback is run
   */
  onClose: PropTypes.func.isRequired,

  /**
   * Allows styling of the modal's content box.
   */
  className: PropTypes.string,

  /**
   * When the modal opens, the modal content receives focus. Pass a valid selector (used in document.querySelector),
   * or an element ref to `initialFocus`, or a function which returns one of those to override the default behavior
   * and focus on a different element.
   *
   * Ideally, the element which receives focus should be the button which closes the modal.
   */
  initialFocus: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(SafeHtmlElement), PropTypes.func])
} : {};
export default Modal;
//# sourceMappingURL=Modal.js.map